import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { getConsent, setConsent, onConsentChange, replayConsent, pushDefaultConsent } from "../../lib/consent";

/**
 * <CookieConsent /> — sticky bottom banner.
 *
 * - HTML establishes denied consent before tags; this replays the saved
 *   choice and updates it when the visitor accepts or rejects.
 * - Persists choice in localStorage for 1 year.
 * - "Cookie Settings" link in the Footer re-opens the banner via the
 *   global `window.dispatchEvent(new CustomEvent("btk:open-cookie-settings"))`
 *   event — see Footer.
 */
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  // Suppress the banner on gated workspace routes where it overlaps form
  // controls. Users land here only after explicit license redemption — we
  // already have a privacy notice in the redemption flow.
  const SUPPRESS_ON_PREFIXES = ["/portal/audit", "/portal/points", "/creative-brief/dashboard"];
  const suppressed = SUPPRESS_ON_PREFIXES.some((p) => location.pathname.startsWith(p));

  // Boot: push default denied + show banner if undecided
  useEffect(() => {
    pushDefaultConsent();
    // Re-apply existing consent after GTM/PostHog load
    replayConsent();
    if (!getConsent()) setVisible(true);

    const onOpen = () => setVisible(true);
    window.addEventListener("btk:open-cookie-settings", onOpen);

    const off = onConsentChange(() => setVisible(false));
    return () => {
      window.removeEventListener("btk:open-cookie-settings", onOpen);
      off();
    };
  }, []);

  if (!visible || suppressed) return null;

  const accept = () => setConsent("granted");
  const reject = () => setConsent("denied");

  return createPortal(
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
      className="fixed inset-x-0 bottom-0 z-[2147483647] px-4 sm:px-6 pb-4 sm:pb-6 pointer-events-none"
    >
      <div
        className="pointer-events-auto max-w-5xl mx-auto rounded-2xl border border-gold/30 backdrop-blur-xl p-5 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-5"
        style={{
          background:
            "linear-gradient(135deg, rgba(8,8,13,0.96) 0%, rgba(4,6,12,0.96) 100%)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.55), 0 0 30px rgba(63, 110, 233,0.12)",
        }}
      >
        <div className="flex-1 text-white/85 text-sm leading-relaxed">
          <span className="block text-[11px] uppercase tracking-[0.22em] text-gold/85 mb-1.5">
            Cookies & Privacy
          </span>
          <p>
            We use cookies and similar technologies for site functionality, analytics, and marketing.
            By selecting <span className="text-white font-semibold">"Accept"</span> you consent to non-essential cookies.
            Choose <span className="text-white font-semibold">"Reject"</span> to limit them to what's strictly necessary.
            See our{" "}
            <Link to="/cookie-policy" className="underline hover:text-gold" data-testid="cookie-banner-policy-link">
              Cookie Policy
            </Link>{" "}
            and{" "}
            <Link to="/privacy-policy" className="underline hover:text-gold" data-testid="cookie-banner-privacy-link">
              Privacy Policy
            </Link>
            .
          </p>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0 self-stretch md:self-auto">
          <button
            type="button"
            onClick={reject}
            data-testid="cookie-reject"
            className="px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.2em] font-semibold text-white/85 border border-white/20 hover:border-white/50 hover:bg-white/5 transition-colors"
          >
            Reject
          </button>
          <button
            type="button"
            onClick={accept}
            data-testid="cookie-accept"
            className="px-7 py-2.5 rounded-full text-xs uppercase tracking-[0.2em] font-bold text-black hover:scale-[1.02] transition-transform"
            style={{
              background: "#3F6EE9",
              boxShadow: "0 0 20px rgba(63, 110, 233,0.32), inset 0 1px 0 rgba(255,255,255,0.35)",
            }}
          >
            Accept
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
